import axios from 'axios';
import { TwitchStream, TwitchGame } from '../types';
import type { ServerChannel } from '../constants/channels';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const api = axios.create({
  baseURL: API_URL,
});

export interface TwitchUser {
  id: string;
  login: string;
  display_name: string;
  profile_image_url: string;
  /** Allowed to create channels on the dial and edit channel rosters */
  channelAdmin?: boolean;
}

export const getTopStreams = async (
  limit: number = 100,
  language?: string,
  gameId?: string,
): Promise<TwitchStream[]> => {
  try {
    const params: { limit: number; language?: string; gameId?: string } = { limit };
    if (language && language !== 'all') {
      params.language = language;
    }
    if (gameId) {
      params.gameId = gameId;
    }
    const response = await api.get<TwitchStream[]>('/api/streams', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching streams:', error);
    throw error;
  }
};

// ————— Categories (games) —————

/** The current top categories on Twitch — powers the picker grid */
export const getTopGames = async (): Promise<TwitchGame[]> => {
  try {
    const response = await api.get<TwitchGame[]>('/api/games/top');
    return response.data;
  } catch (error) {
    console.error('Error fetching top games:', error);
    return [];
  }
};

/** Resolve category ids (e.g. from a /g/ deep link) to name + box art */
export const getGamesByIds = async (ids: string[]): Promise<TwitchGame[]> => {
  if (ids.length === 0) return [];
  try {
    const response = await api.get<TwitchGame[]>('/api/games', {
      params: { ids: ids.join(',') },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching games:', error);
    return [];
  }
};

/** Search categories by name for the picker's search box */
export const searchCategories = async (query: string): Promise<TwitchGame[]> => {
  if (!query.trim()) return [];
  try {
    const response = await api.get<{ data: TwitchGame[] }>('/api/search/categories', {
      params: { query: query.trim(), first: 12 },
    });
    return response.data.data || [];
  } catch (error) {
    console.error('Error searching categories:', error);
    return [];
  }
};

// ————— Login-free share links —————

/** Turn a list of logins into a short share code (no account needed) */
export const createShareCode = async (streams: string[]): Promise<string | null> => {
  try {
    const response = await api.post<{ code: string }>('/api/share', { streams });
    return response.data.code || null;
  } catch (error) {
    console.error('Error creating share link:', error);
    return null;
  }
};

/** Fetch the list behind a /s/CODE link */
export const getSharedList = async (code: string): Promise<string[] | null> => {
  try {
    const response = await api.get<{ streams: string[] }>(`/api/share/${encodeURIComponent(code)}`);
    return Array.isArray(response.data.streams) ? response.data.streams : null;
  } catch {
    return null;
  }
};

export const getChatMetrics = async (): Promise<Record<string, { chatRate: number; messageCount: number }>> => {
  try {
    const response = await api.get('/api/chat-metrics');
    return response.data;
  } catch (error) {
    console.error('Error fetching chat metrics:', error);
    return {};
  }
};

// Auth functions
export const getAuthUrl = async (): Promise<string> => {
  const response = await api.get<{ url: string }>('/api/auth/twitch');
  return response.data.url;
};

export const getUser = async (session: string): Promise<TwitchUser | null> => {
  try {
    const response = await api.get<{ user: TwitchUser }>('/api/auth/user', {
      params: { session },
    });
    return response.data.user;
  } catch {
    return null;
  }
};

export const logout = async (session: string): Promise<void> => {
  await api.post('/api/auth/logout', null, {
    params: { session },
  });
};

export const getFollowedStreams = async (session: string): Promise<TwitchStream[]> => {
  try {
    const response = await api.get<TwitchStream[]>('/api/followed-streams', {
      params: { session },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching followed streams:', error);
    return [];
  }
};

/** One watched channel, independent of the rankings. Errors are distinct from offline. */
export const getStreamByLogin = async (login: string): Promise<TwitchStream | null> => {
  const response = await api.get<TwitchStream[]>('/api/streams/by-login', {
    params: { logins: login },
  });
  return response.data.find(stream => stream.user_login.toLowerCase() === login.toLowerCase()) ?? null;
};

/** Live streams for an explicit list of logins — absent means offline */
export const getStreamsByLogin = async (logins: string[]): Promise<TwitchStream[]> => {
  if (logins.length === 0) return [];
  try {
    const response = await api.get<TwitchStream[]>('/api/streams/by-login', {
      params: { logins: logins.slice(0, 100).join(',') },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching streams by login:', error);
    return [];
  }
};

/** Profiles (avatar, display name) so offline list entries still have a face */
export const getUsersByLogin = async (logins: string[]): Promise<TwitchUser[]> => {
  if (logins.length === 0) return [];
  try {
    const response = await api.get<TwitchUser[]>('/api/users/by-login', {
      params: { logins: logins.slice(0, 100).join(',') },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching users by login:', error);
    return [];
  }
};

/** Roster of a saved list (crew or user handle) — null when it doesn't exist */
export const getListStreams = async (handle: string): Promise<string[] | null> => {
  try {
    const response = await api.get<{ streams?: string[] }>(`/api/list/${handle}`);
    return Array.isArray(response.data.streams) ? response.data.streams : null;
  } catch (error) {
    return null;
  }
};

// ————— Channels —————
// The dial's roster channels. Reading is public; the writes are for channel
// admins (the server answers 403 otherwise) and every one hands back the
// whole catalogue, rosters included, so the dial re-racks from that.

/** The catalogue with rosters — null when the API is unreachable (or too old to have it) */
export const getChannels = async (): Promise<ServerChannel[] | null> => {
  try {
    const response = await api.get<{ channels: ServerChannel[] }>('/api/channels');
    return Array.isArray(response.data?.channels) ? response.data.channels : null;
  } catch (error) {
    console.error('Error fetching channels:', error);
    return null;
  }
};

export interface ChannelPatch {
  label?: string;
  /** '' = the crews row, 'just' = the Just row */
  tag?: 'just' | '';
  streams?: string[];
}

export interface ChannelMutation {
  channel?: ServerChannel;
  channels: ServerChannel[];
}

const apiErrorMessage = (error: unknown, fallback: string): string => {
  const message = (error as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  return typeof message === 'string' && message ? message : fallback;
};

export const createChannel = async (session: string, input: ChannelPatch & { label: string; id?: string }): Promise<ChannelMutation> => {
  try {
    const response = await api.post<ChannelMutation>('/api/channels', { sessionId: session, ...input });
    return response.data;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'Could not create the channel'));
  }
};

export const updateChannel = async (session: string, id: string, patch: ChannelPatch): Promise<ChannelMutation> => {
  try {
    const response = await api.put<ChannelMutation>(`/api/channels/${encodeURIComponent(id)}`, { sessionId: session, ...patch });
    return response.data;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'Could not save the channel'));
  }
};

export const deleteChannel = async (session: string, id: string): Promise<ChannelMutation> => {
  try {
    const response = await api.delete<ChannelMutation>(`/api/channels/${encodeURIComponent(id)}`, { data: { sessionId: session } });
    return response.data;
  } catch (error) {
    throw new Error(apiErrorMessage(error, 'Could not delete the channel'));
  }
};

export const getRecommendedStreams = async (session: string, language?: string): Promise<TwitchStream[]> => {
  try {
    const params: { session: string; language?: string } = { session };
    if (language && language !== 'all') {
      params.language = language;
    }
    const response = await api.get<TwitchStream[]>('/api/recommended-streams', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching recommended streams:', error);
    return [];
  }
};

// Visitor tracking
export const trackConnect = async (visitorId?: string, stream?: string): Promise<{ visitorId: string; active: number; total: number; peak: number; watchTime: number }> => {
  try {
    const response = await api.post('/api/connect', { visitorId, stream });
    return response.data;
  } catch (error) {
    console.error('Error tracking connect:', error);
    return { visitorId: visitorId || '', active: 0, total: 0, peak: 0, watchTime: 0 };
  }
};

export const trackDisconnect = async (visitorId: string): Promise<void> => {
  try {
    await api.post('/api/disconnect', { visitorId });
  } catch (error) {
    console.error('Error tracking disconnect:', error);
  }
};
